import { GenericDocumentDto, GroupContractDto, GroupContractType } from "api/contract";
import { EntityDetailsHeader, EntityDetailsHeaderConfig } from "components/display/EntityDetailsHeader";
import { EntityDetailsStat, EntityDetailsStatConfig } from "components/display/EntityDetailsStat";
import GenericTabs from "components/navigation/Tabs/Tabs";
import { Calendar, CheckCircle2, Clock, Home, Paperclip, Umbrella, User } from "lucide-react";
import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { contractsAPI } from "services/swagger-api";
import { ActivityTimeline } from "features/activity-feed";
import { GenericContractsTable } from "./GenericContractTable";
import { GroupContractDocuments } from "./GroupContractDocuments";

const formatDate = (value?: string | Date | null) => {
  if (!value) return "—";
  const date = typeof value === "string" ? new Date(value) : value;
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("fr-FR");
};


interface GroupContractDetailsProps {
  /** Périmètre courtier (route /mes-contrats-collectifs) : endpoints périmétrés au lieu des endpoints admin. */
  brokerScope?: boolean;
}

export const GroupContractDetails: FC<GroupContractDetailsProps> = ({ brokerScope = false }) => {
  const { groupContractId } = useParams<{ groupContractId: string }>();
  const navigate = useNavigate();
  const [groupContract, setGroupContract] = useState<GroupContractDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number | string>(1);
  const [documents, setDocuments] = useState<GenericDocumentDto[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);

  useEffect(() => {
    const fetchGroupContract = async () => {
      if (!groupContractId) {
        return;
      }
      try {
        setIsLoading(true);
        setError(null);
        const groupContractData = await contractsAPI.getGroupContractByID({
          groupContractId
        });
        setGroupContract(groupContractData);
      } catch (err) {
        console.error("Erreur lors du chargement du contrat de groupe:", err);
        setError("Impossible de charger les détails du contrat de groupe");
      } finally {
        setIsLoading(false);
      }
    };

    fetchGroupContract();
  }, [groupContractId]);

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!groupContractId) {
        return;
      }
      try {
        setDocumentsLoading(true);
        const response = await contractsAPI.getGroupContractDocuments({ groupContractId });
        setDocuments(response?.documents ?? []);
      } catch (err) {
        console.error("Erreur lors du chargement des documents du contrat de groupe:", err);
        setDocuments([]);
      } finally {
        setDocumentsLoading(false);
      }
    };

    fetchDocuments();
  }, [groupContractId]);

  const subscriberName = useMemo(() => {
    const s: any = groupContract?.subscriber;
    if (s) {
      return s.name || s.displayName || s.designation || `${s.lastName ?? ""} ${s.firstName ?? ""}`.trim() || "—";
    }
    return groupContract?.owner ?? "—";
  }, [groupContract]);

  if (isLoading || !groupContractId) {
    return (
      <section className="w-full overflow-hidden border bg-white rounded-2xl p-8">
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Chargement...</p>
        </div>
      </section>
    );
  }

  if (error || !groupContract) {
    return (
      <section className="w-full overflow-hidden border bg-white rounded-2xl p-8">
        <div className="flex items-center justify-center h-64">
          <p className="text-red-500">{error || "Contrat de groupe introuvable"}</p>
        </div>
      </section>
    );
  }

  const isMaster = groupContract.type === GroupContractType.Cadre || groupContract.isMaster;
  const contractTypeLabel = isMaster ? "Contrat Cadre" : "Contrat de groupe";

  const headerConfig: EntityDetailsHeaderConfig = {
    icon: <Home className="text-primary w-8 h-8" />,
    title: contractTypeLabel,
    subtitle: `#${groupContract.contractIdentifier}`,
    infoSections: [
      {
        icon: User,
        label: "Souscripteur",
        value: subscriberName,
        disabled: true
      }
    ],
    badge: {
      label: "Contrat actif",
      variant: "green",
      icon: CheckCircle2
    },
    metadata: {
      icon: Calendar,
      text: `Créé le ${formatDate(groupContract.creationTime)}`
    }
  };

  // Products and insurers are deduplicated by the back over every active tunnel.
  const productLabel = groupContract.products?.map((p) => p.label).join(", ") || "—";
  const insurersLabel = groupContract.insurers?.join(", ") || "—";

  const statsConfig: EntityDetailsStatConfig = {
    stats: [
      { label: "Type", value: productLabel },
      { label: "Assureurs", value: insurersLabel },
      { label: "Échéance principale", value: groupContract.mainDueDate || "—" },
      { label: "Périodicité", value: groupContract.periodicity || "—" },
      // Groupe : toujours quittancé par le souscripteur (paymentByAliments false par définition).
      {
        label: "Quittancé par",
        value: groupContract.paymentByAliments ? "Assurés adhérents" : "Souscripteur"
      },
      { label: "Date d'effet", value: formatDate(groupContract.effectiveDate) },
      { label: "Prime annualisée", value: (groupContract as any).annualPremium || "—" }
    ]
  };

  const aliments = groupContract.activeContractsCount ?? 0;

  return (
    <section className="w-full overflow-hidden border bg-white rounded-2xl">
      <EntityDetailsHeader config={headerConfig} />
      <div className="p-8 space-y-8 shadow-inner">
        <EntityDetailsStat config={statsConfig} />

        <GenericTabs defaultTab={activeTab} onChange={setActiveTab}>
          <GenericTabs.Item
            label="Aliments du contrat"
            icon={<Umbrella className="w-4 h-4" />}
            badge={aliments}
            value={1}
          />
          <GenericTabs.Item
            label="Documents contractuels"
            icon={<Paperclip className="w-4 h-4" />}
            badge={documents.length}
            value={2}
          />
          <GenericTabs.Item
            label="Historique"
            icon={<Clock className="w-4 h-4" />}
            value={3}
          />

          <GenericTabs.Content value={1}>
            <GenericContractsTable
              forcedFilters={{ groupContractIds: [groupContractId] }}
              brokerScope={brokerScope}
              onViewClick={(contract) => navigate(`/mes-contrats/details/${contract.id}`)}
              title="Liste des aliments"
              onCreateConfig={{
                label: "Ajouter un aliment",
                onClick: () => { }
              }}
            />
          </GenericTabs.Content>

          <GenericTabs.Content value={2}>
            <GroupContractDocuments
              documents={documents}
              isLoading={documentsLoading}
            />
          </GenericTabs.Content>

          <GenericTabs.Content value={3}>
            <div className="h-[32rem]">
              <ActivityTimeline groupContractId={groupContractId} />
            </div>
          </GenericTabs.Content>
        </GenericTabs>
      </div>
    </section>
  );
};
